import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TrackNowButton } from '../ui/TrackNowButton';
import { Dialog } from "primereact/dialog";
import { SuccessIllustration, WarningIllustration, ErrorIllustration, QuestionIllustration, DangerConfirmIllustration } from './DialogIllustrations';
import './CustomDialog.css';

const useDialog = (options) => {
  const optionsRef = useRef(options || {});

  useEffect(() => {
    optionsRef.current = options || {};
  }, [options]);

  const extractText = (value) => {
    if (value === null || value === undefined) return "";
    if (typeof value === "string" || typeof value === "number") return String(value);
    if (Array.isArray(value)) return value.map((item) => extractText(item)).join(" ");
    if (typeof value === "object" && value.props?.children !== undefined) {
      return extractText(value.props.children);
    }
    return String(value);
  };

  const autoDetectType = (msg) => {
    if (!msg) return 'info';
    const text = extractText(msg);
    const lower = text.toLowerCase();
    if (/(error|failed|wrong|invalid|cannot|unable|not found|not able)/i.test(lower)) return 'error';
    if (/(warning|caution|please|select|choose|required|must|should)/i.test(lower)) return 'warning';
    if (/(confirm|delete|remove|defer|deferred|sure|proceed|are you sure)/i.test(lower)) return 'confirm';
    if (/(success|saved|completed|done|created|updated|deleted|confirmed|successfully|approved|rejected)/i.test(lower)) return 'success';
    return 'info';
  };

  const [title, setTitle] = useState(options.title || "Information");
  const [message, setMessage] = useState(options.message || "Alert");
  const [type, setType] = useState(options.type || autoDetectType(options.message));
  // const [onSubmit, setSubmit] = useState(options.onSubmit);
  // const [onClose, setClose] = useState(options.onClose);
  //type?: "error" | "warning" | "info" | "success" | "confirm";
  const [show, setShow] = useState(false);

  //   handleClose: () => void;
  //   handleAction?: () => void;

  const getIllustration = () => {
    switch(type) {
      case 'success': return <SuccessIllustration />;
      case 'warning': return <WarningIllustration />;
      case 'error': return <ErrorIllustration />;
      case 'confirm': return <DangerConfirmIllustration />;
      default: return <QuestionIllustration />;
    }
  };

  const getTitle = () => {
    if (title && title !== "Information" && title !== "Confirmation") return title;
    switch(type) {
      case 'success': return 'Success!';
      case 'warning': return 'Warning!';
      case 'error': return 'Wrong!';
      case 'confirm': return 'Confirmation';
      default: return 'Information';
    }
  };

  const messageText = extractText(message).toLowerCase();
  const isDeleteLike = /(delete|remove)/i.test(messageText);
  const isDeferLike = /(defer|deferred)/i.test(messageText);
  const confirmLabel = isDeleteLike
    ? "Yes, Delete!"
    : isDeferLike
      ? "Yes, Defer"
      : "Yes, Continue";
  const cancelLabel = isDeleteLike || isDeferLike ? "No, Keep It." : "Cancel";

  const confirmButtonVariant = options?.confirmVariant || ((isDeleteLike || isDeferLike) ? "destructive" : "primary");
  const cancelButtonVariant = options?.cancelVariant || "tertiary";

  useEffect(() => {
    if (options?.title) setTitle(options.title);
    if (options?.message) setMessage(options.message);
    if (options?.type) setType(options.type);
    // if (options?.onSubmit) setSubmit(options.onSubmit);
    // if (options?.onClose) setClose(options.onClose);
    //eslint-disable-next-line
  }, [options]);

  // useEffect(() => {
  //   console.log("usePageTitle");
  //   if (options?.appTitle) {
  //     if (options?.pageTitle !== "") {
  //       const titleArray = options.appTitle.split("|");
  //       setPageTitle(
  //         titleArray[0] + " | " + options?.pageTitle + " | " + titleArray[1]
  //       );
  //       console.log("titleArray", titleArray, pageTitle);
  //     } else {
  //       setPageTitle(options.appTitle);
  //     }
  //   }
  //   //eslint-disable-next-line
  // }, []);
  const showDialog = (pTitle, pMessage, pType) => {
    if (pTitle) setTitle(() => pTitle);
    if (pMessage) setMessage(() => pMessage);
    setType(() => pType || autoDetectType(pMessage || message));
    setShow(() => true);
  };

  const handleSubmit = useCallback(() => {
    setShow(() => false);
    console.log("Closing OK", optionsRef.current.onSubmit);
    if (optionsRef.current.onSubmit) {
      optionsRef.current.onSubmit();
    }
  }, []);

  const handleClose = useCallback(() => {
    setShow(() => false);
    if (optionsRef.current.onClose) {
      optionsRef.current.onClose();
    }
  }, []);

  const closeDialog = () => {
    setShow(() => false);
  };

  const footerContent = useMemo(
    () => (
      <div className={`dialog-buttons ${type === "confirm" ? "confirm-actions" : ""}`}>
        {type === "confirm" ? (
          <>
            <TrackNowButton variant={cancelButtonVariant} onClick={handleClose}>
              {cancelLabel}
            </TrackNowButton>
            <TrackNowButton
              variant={confirmButtonVariant}
              onClick={handleSubmit}
            >
              {confirmLabel}
            </TrackNowButton>
          </>
        ) : (
          <TrackNowButton
            variant="primary"
            onClick={handleSubmit}
          >
            OK
          </TrackNowButton>
        )}
      </div>
    ),
    [cancelButtonVariant, cancelLabel, confirmButtonVariant, confirmLabel, handleClose, handleSubmit, type]
  );

  const AlertDialog = useCallback(() => {
    return (
      <Dialog
        visible={show}
        style={{ width: "30vw", minWidth: "400px" }}
        onHide={handleClose}
        footer={footerContent}
        baseZIndex={1000}
        className={`custom-dialog ${type === "confirm" ? "custom-dialog--confirm" : ""}`}
        closable={false}
      >
        <div className="dialog-illustration">
          {getIllustration()}
        </div>
        <h2 className={`dialog-title ${type || 'info'}`}>{getTitle()}</h2>
        <div className='dialog-message'>{message}</div>
      </Dialog>
    );
  }, [footerContent, handleClose, message, show, type]);

  return {
    AlertDialog,
    showDialog,
    closeDialog,
  };
};

export default useDialog;
